import { sealRecord, openRecord, openRecords, sealPatch } from './localIndex';
import { db, normalizeFolderId } from './db';

const JOURNAL_AAD = 'tresor-journal-v1';
export const META_LAST_EVENT = 'journalLastEventId';
const META_DEVICE_TAG = 'journalDeviceTag';
const META_DEVICE_SEQ = 'journalDeviceSeq';
const META_BURNED_SEQ = 'journalBurnedSeq';

/**
 * Reihenfolge- und Vollstaendigkeitsnachweis fuer das Journal.
 *
 * Das Problem: Jeder Eintrag ist fuer sich verschluesselt und authentifiziert, aber nichts
 * band bisher seine POSITION. Der Server konnte Eintraege weglassen oder umsortieren, ohne
 * dass es auffiel: eine Datei fehlt still in der Liste, eine alte Umbenennung gewinnt gegen
 * eine neue. Kein Datenabfluss, aber eine Luecke bei einem Produkt, das mit Nachpruefbarkeit
 * wirbt.
 *
 * Die Loesung: jeder Eintrag traegt IM VERSCHLUESSELTEN TEIL eine Geraetekennung und einen
 * je Geraet fortlaufenden Zaehler. Beides steht damit unter dem AES-GCM-Tag und laesst sich
 * nicht faelschen. Beim Abgleich muessen die Zaehler eines Geraets eine luekenlose Reihe ab 1
 * ergeben. Fehlt eine Zahl, hat jemand einen Eintrag unterschlagen.
 *
 * Warum je Geraet und nicht global: der Tresor erlaubt zwei Geraete. Ein globaler Zaehler
 * haette bei gleichzeitigen Schreibvorgaengen kollidiert und echte Konflikte wie Angriffe
 * aussehen lassen. Je Geraet ist die Reihe eindeutig, auch wenn beide gleichzeitig schreiben.
 *
 * Grenze, die bleibt: was ein Geraet nie gesehen hat, kann es nicht vermissen. Unterschlaegt
 * der Server die JUENGSTEN Eintraege des ANDEREN Geraets, faellt das hier nicht auf. Die
 * eigenen fehlenden Eintraege erkennt jedes Geraet, weil es seinen eigenen Stand kennt.
 */
function randomTag() {
  const bytes = window.crypto.getRandomValues(new Uint8Array(8));
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function getDeviceTag() {
  const row = await db.meta.get(META_DEVICE_TAG);
  if (row?.value) return row.value;
  const tag = randomTag();
  await db.meta.put({ key: META_DEVICE_TAG, value: tag });
  return tag;
}

/**
 * Naechste Zaehlernummer reservieren. Laeuft in einer Dexie-Transaktion, damit zwei
 * Browser-Tabs desselben Tresors nicht dieselbe Nummer ziehen.
 */
async function reserveDeviceSeq() {
  return db.transaction('rw', db.meta, async () => {
    const row = await db.meta.get(META_DEVICE_SEQ);
    const next = (row?.value || 0) + 1;
    await db.meta.put({ key: META_DEVICE_SEQ, value: next });
    return next;
  });
}

/**
 * Eine reservierte Nummer, die nie beim Server ankam, festhalten. Sonst meldet der eigene
 * Abgleich spaeter eine Luecke, die kein Angriff ist, sondern ein abgebrochener Upload.
 */
async function burnDeviceSeq(seq) {
  try {
    const row = await db.meta.get(META_BURNED_SEQ);
    const list = Array.isArray(row?.value) ? row.value : [];
    if (!list.includes(seq)) list.push(seq);
    await db.meta.put({ key: META_BURNED_SEQ, value: list.slice(-500) });
  } catch (_) { /* Der Merker ist Komfort, kein Muss */ }
}

async function getBurnedSeqs() {
  const row = await db.meta.get(META_BURNED_SEQ);
  return new Set(Array.isArray(row?.value) ? row.value : []);
}

export function createClientId() {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function bytesToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export async function encryptJournalPayload(masterKey, event) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(event));
  const aad = new TextEncoder().encode(JOURNAL_AAD);
  const ct = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: aad },
    masterKey,
    plaintext
  );
  const combined = new Uint8Array(iv.length + ct.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ct), iv.length);
  return bytesToBase64(combined);
}

export async function decryptJournalPayload(masterKey, payloadB64) {
  const combined = base64ToBytes(payloadB64);
  const iv = combined.slice(0, 12);
  const data = combined.slice(12);
  const aad = new TextEncoder().encode(JOURNAL_AAD);
  const pt = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, additionalData: aad },
    masterKey,
    data
  );
  return JSON.parse(new TextDecoder().decode(pt));
}

async function getLastEventId() {
  const row = await db.meta.get(META_LAST_EVENT);
  return row?.value || 0;
}

async function setLastEventId(id) {
  await db.meta.put({ key: META_LAST_EVENT, value: id });
}

export async function appendJournalEvent(authenticatedFetch, masterKey, event) {
  const dev = await getDeviceTag();
  const seq = await reserveDeviceSeq();
  const payload = {
    v: 2,
    ts: Date.now(),
    dev,
    seq,
    ...event
  };
  const encrypted_payload = await encryptJournalPayload(masterKey, payload);
  let res;
  try {
    res = await authenticatedFetch('/api/tresor/journal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ encrypted_payload, client_timestamp: payload.ts })
    });
  } catch (netErr) {
    await burnDeviceSeq(seq);
    throw netErr;
  }
  if (!res.ok) {
    await burnDeviceSeq(seq);
    throw new Error('journal_append_failed');
  }
  const data = await res.json();
  if (data.event_id) await setLastEventId(data.event_id);
  return data.event_id;
}

async function folderByClientId(clientId) {
  if (!clientId || clientId === 'root') return null;
  return db.folders.where('clientId').equals(clientId).first();
}

async function resolveLocalFolderId(parentClientId) {
  const key = normalizeFolderId(parentClientId);
  if (key === 'root') return 'root';
  const folder = await folderByClientId(key);
  return folder ? folder.id : null;
}

// Collect a folder clientId plus every descendant folder clientId (BFS over parentClientId).
async function collectSubtreeFolderClientIds(rootClientId) {
  const all = await db.folders.toArray();
  const childrenByParent = new Map();
  for (const f of all) {
    const p = normalizeFolderId(f.parentClientId);
    if (!childrenByParent.has(p)) childrenByParent.set(p, []);
    childrenByParent.get(p).push(f.clientId);
  }
  const result = [];
  const queue = [rootClientId];
  while (queue.length) {
    const cid = queue.shift();
    result.push(cid);
    for (const child of childrenByParent.get(cid) || []) queue.push(child);
  }
  return result;
}

async function applyJournalEvent(event) {
  switch (event.type) {
    case 'folder_create': {
      const existingRaw = await folderByClientId(event.clientId);
      if (existingRaw) {
        const existing = await openRecord('folder', existingRaw);
        if (event.name && existing.name !== event.name) {
          await db.folders.update(existing.id, await sealPatch('folder', existingRaw, { name: event.name }));
        }
        return;
      }
      const parentKey = normalizeFolderId(event.parentClientId);
      const parentId = parentKey === 'root' ? 'root' : await resolveLocalFolderId(parentKey);
      await db.folders.add(await sealRecord('folder', {
        clientId: event.clientId,
        parentClientId: parentKey,
        parentId: parentId ?? 'root',
        name: event.name,
        createdAt: event.ts || Date.now()
      }));
      break;
    }
    case 'folder_rename': {
      const folder = await folderByClientId(event.clientId);
      if (folder && event.name) await db.folders.update(folder.id, await sealPatch('folder', folder, { name: event.name }));
      break;
    }
    case 'folder_delete': {
      const folder = await folderByClientId(event.clientId);
      if (folder) {
        const files = await db.files.where('folderClientId').equals(event.clientId).toArray();
        for (const f of files) {
          await db.files.delete(f.id);
          await db.fileBlobs.delete(f.id);
        }
        await db.folders.delete(folder.id);
      }
      break;
    }
    case 'file_add': {
      const existing = await db.files.where('clientId').equals(event.clientId).first();
      const folderClientId = normalizeFolderId(event.parentClientId);
      const folderId = folderClientId === 'root' ? 'root' : await resolveLocalFolderId(folderClientId);
      const record = {
        clientId: event.clientId,
        folderClientId,
        folderId: folderId ?? 'root',
        name: event.name,
        type: event.fileType || 'file',
        size: event.sizeLabel || '',
        sizeBytes: event.sizeBytes || 0,
        originalType: event.mimeType || 'application/octet-stream',
        encryptionId: event.encryptionId,
        wrappedFileKey: event.wrappedFileKey || null,
        uploadId: event.uploadId || null,
        totalChunks: event.totalChunks || null,
        chunkSize: event.chunkSize || null,
        createdAt: event.ts || Date.now()
      };
      const sealedRecord = await sealRecord('file', record);
      if (existing) {
        await db.files.update(existing.id, sealedRecord);
      } else {
        await db.files.add(sealedRecord);
      }
      break;
    }
    case 'file_rename': {
      const file = await db.files.where('clientId').equals(event.clientId).first();
      if (!file || !event.name) return;
      const patch = { name: event.name };
      if (event.fileType) patch.type = event.fileType;
      await db.files.update(file.id, await sealPatch('file', file, patch));
      break;
    }
    case 'folder_move': {
      // Ordner samt Inhalt unter einen anderen Elternordner. Ziel unbekannt oder im eigenen
      // Teilbaum (waere ein Kreis): Eintrag ignorieren, der Ordner bleibt, wo er ist.
      const folder = await folderByClientId(event.clientId);
      if (!folder) return;
      const parentKey = normalizeFolderId(event.parentClientId);
      let parentId = 'root';
      if (parentKey !== 'root') {
        const parent = await folderByClientId(parentKey);
        if (!parent) return;
        const subtree = await collectSubtreeFolderClientIds(event.clientId);
        if (subtree.includes(parentKey)) return;
        parentId = parent.id;
      }
      await db.folders.update(folder.id, { parentClientId: parentKey, parentId });
      break;
    }
    case 'file_move': {
      const file = await db.files.where('clientId').equals(event.clientId).first();
      if (!file) return;
      const folderClientId = normalizeFolderId(event.parentClientId);
      const folderId = folderClientId === 'root' ? 'root' : await resolveLocalFolderId(folderClientId);
      await db.files.update(file.id, { folderClientId, folderId: folderId ?? 'root' });
      break;
    }
    case 'folder_trash': {
      // Recursive recycle bin for a whole subtree: mark the folder, every descendant
      // folder, and every file within them as trashed (shared timestamp) — records stay
      // so the subtree can be restored intact. Server keeps each file's chunks + quota.
      // Schon vorher einzeln Geloeschtes behaelt seinen Zeitpunkt, damit es beim Wiederherstellen
      // des Ordners nicht ungefragt mit zurueckkommt.
      const folder = await folderByClientId(event.clientId);
      if (!folder) break;
      const ts = event.ts || Date.now();
      const subtree = await collectSubtreeFolderClientIds(event.clientId);
      for (const cid of subtree) {
        const f = await folderByClientId(cid);
        if (f && !f.trashedAt) await db.folders.update(f.id, { trashedAt: ts });
        const files = await db.files.where('folderClientId').equals(cid).toArray();
        for (const file of files) if (!file.trashedAt) await db.files.update(file.id, { trashedAt: ts });
      }
      break;
    }
    case 'folder_restore': {
      // Nur, was mit dem Ordner zusammen geloescht wurde (Zeitpunkt gleich oder spaeter).
      const folder = await folderByClientId(event.clientId);
      if (!folder) break;
      const since = folder.trashedAt || 0;
      const subtree = await collectSubtreeFolderClientIds(event.clientId);
      for (const cid of subtree) {
        const f = await folderByClientId(cid);
        if (f && f.trashedAt && f.trashedAt >= since) await db.folders.update(f.id, { trashedAt: null });
        const files = await db.files.where('folderClientId').equals(cid).toArray();
        for (const file of files) if (file.trashedAt && file.trashedAt >= since) await db.files.update(file.id, { trashedAt: null });
      }
      break;
    }
    case 'file_trash': {
      // Recycle bin: mark the file as trashed but KEEP the record so it stays visible in
      // the Papierkorb and can be restored. Server keeps the chunks + quota reserved.
      const file = await db.files.where('clientId').equals(event.clientId).first();
      if (file) await db.files.update(file.id, { trashedAt: event.ts || Date.now() });
      break;
    }
    case 'file_restore': {
      const file = await db.files.where('clientId').equals(event.clientId).first();
      if (file) await db.files.update(file.id, { trashedAt: null });
      break;
    }
    case 'file_delete': {
      // PERMANENT delete (client 14-day window elapsed, or "Papierkorb leeren").
      const file = await db.files.where('clientId').equals(event.clientId).first();
      if (file) {
        await db.files.delete(file.id);
        await db.fileBlobs.delete(file.id);
      }
      break;
    }
    default:
      break;
  }
}

/**
 * Vollstaendigkeits- und Reihenfolgeprueckung ueber die Zaehler aus den entschluesselten
 * Eintraegen. Erwartet wird je Geraet die luekenlose Reihe 1, 2, 3, … in aufsteigender
 * Reihenfolge. Alteintraege ohne Zaehler (v1) bleiben aussen vor, sonst wuerde jeder
 * Bestandstresor sofort Alarm schlagen.
 */
function auditJournalSequence(seen, ownTag, burned) {
  const problems = [];
  for (const [dev, info] of seen) {
    const nums = [...info.numbers].sort((a, b) => a - b);
    if (nums.length === 0) continue;

    if (nums[0] !== 1) {
      problems.push({ dev, kind: 'start', detail: `beginnt bei ${nums[0]} statt 1` });
    }
    for (const dup of info.duplicates || []) {
      problems.push({ dev, kind: 'duplicate', detail: `Eintrag ${dup} kam doppelt` });
    }
    for (let i = 1; i < nums.length; i++) {
      const prev = nums[i - 1];
      const cur = nums[i];
      for (let missing = prev + 1; missing < cur; missing++) {
        // Eigene abgebrochene Uploads sind erklaerte Luecken, kein Angriff.
        if (dev === ownTag && burned.has(missing)) continue;
        problems.push({ dev, kind: 'gap', detail: `Nummer ${missing} fehlt` });
      }
    }
    if (info.outOfOrder) {
      problems.push({ dev, kind: 'order', detail: 'Eintraege kamen nicht in ihrer Reihenfolge' });
    }
  }
  return problems;
}

export async function syncJournalFromServer(authenticatedFetch, masterKey) {
  let since = await getLastEventId();
  let applied = 0;
  let legacy = 0;

  const ownTag = await getDeviceTag();
  const burned = await getBurnedSeqs();
  // dev -> { numbers: Set, last: number, outOfOrder: bool }
  const seen = new Map();

  for (;;) {
    const res = await authenticatedFetch(`/api/tresor/journal?since=${since}`);
    if (!res.ok) throw new Error('journal_sync_failed');
    const data = await res.json();
    const events = data.events || [];
    if (events.length === 0) break;

    for (const row of events) {
      const event = await decryptJournalPayload(masterKey, row.encrypted_payload);

      if (typeof event.seq === 'number' && typeof event.dev === 'string') {
        let info = seen.get(event.dev);
        if (!info) {
          info = { numbers: new Set(), duplicates: new Set(), last: 0, outOfOrder: false };
          seen.set(event.dev, info);
        }
        // Ein Set verschluckt Doppel, bevor die Pruefung sie sieht. Wiedereingespielte
        // Eintraege sollen aber als solche gemeldet werden und nicht als Sortierfehler.
        if (info.numbers.has(event.seq)) info.duplicates.add(event.seq);
        else if (event.seq < info.last) info.outOfOrder = true;
        info.numbers.add(event.seq);
        info.last = Math.max(info.last, event.seq);
      } else {
        legacy += 1;
      }

      await applyJournalEvent(event);
      since = row.id;
      applied += 1;
    }
    await setLastEventId(since);
    if (events.length < 500) break;
  }

  const problems = auditJournalSequence(seen, ownTag, burned);
  return { applied, legacy, problems, checked: [...seen.values()].reduce((n, i) => n + i.numbers.size, 0) };
}

export async function bootstrapLocalJournal(authenticatedFetch, masterKey, helpers) {
  const res = await authenticatedFetch('/api/tresor/journal?since=0');
  if (!res.ok) return;
  const data = await res.json();
  if ((data.events || []).length > 0) return;

  const folders = await openRecords('folder', await db.folders.toArray());
  const files = await openRecords('file', await db.files.toArray());
  if (folders.length === 0 && files.length === 0) return;

  const { getFileType, formatBytes } = helpers;

  for (const folder of folders) {
    let clientId = folder.clientId;
    if (!clientId) {
      clientId = createClientId();
      await db.folders.update(folder.id, { clientId });
    }
    let parentClientId = folder.parentClientId;
    if (!parentClientId) {
      const pid = normalizeFolderId(folder.parentId);
      if (pid === 'root') parentClientId = 'root';
      else {
        const parent = await db.folders.get(Number(pid));
        parentClientId = parent?.clientId || 'root';
        await db.folders.update(folder.id, { parentClientId });
      }
    }
    await appendJournalEvent(authenticatedFetch, masterKey, {
      type: 'folder_create',
      clientId,
      parentClientId,
      name: folder.name
    });
  }

  for (const file of files) {
    let clientId = file.clientId;
    if (!clientId) {
      clientId = createClientId();
      await db.files.update(file.id, { clientId });
    }
    let folderClientId = file.folderClientId;
    if (!folderClientId) {
      const fid = normalizeFolderId(file.folderId);
      if (fid === 'root') folderClientId = 'root';
      else {
        const folder = await db.folders.get(Number(fid));
        folderClientId = folder?.clientId || 'root';
        await db.files.update(file.id, { folderClientId });
      }
    }
    await appendJournalEvent(authenticatedFetch, masterKey, {
      type: 'file_add',
      clientId,
      parentClientId: folderClientId,
      name: file.name,
      encryptionId: file.encryptionId,
      wrappedFileKey: file.wrappedFileKey || null,
      uploadId: file.uploadId || null,
      totalChunks: file.totalChunks || null,
      chunkSize: file.chunkSize || null,
      sizeBytes: file.sizeBytes || 0,
      sizeLabel: file.size || formatBytes(file.sizeBytes || 0),
      mimeType: file.originalType || 'application/octet-stream',
      fileType: file.type || getFileType(file.name)
    });
  }
}

export async function appendFolderCreate(authenticatedFetch, masterKey, { clientId, parentClientId, name }) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'folder_create',
    clientId,
    parentClientId,
    name
  });
}

export async function appendFolderRename(authenticatedFetch, masterKey, { clientId, name }) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'folder_rename',
    clientId,
    name
  });
}

export async function appendFolderDelete(authenticatedFetch, masterKey, { clientId }) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'folder_delete',
    clientId
  });
}

export async function appendFileRename(authenticatedFetch, masterKey, { clientId, name, fileType }) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'file_rename',
    clientId,
    name,
    fileType
  });
}

export async function appendFolderMove(authenticatedFetch, masterKey, { clientId, parentClientId }) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'folder_move',
    clientId,
    parentClientId
  });
}

export async function appendFileMove(authenticatedFetch, masterKey, { clientId, parentClientId }) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'file_move',
    clientId,
    parentClientId
  });
}

export async function appendFolderTrash(authenticatedFetch, masterKey, { clientId }) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'folder_trash',
    clientId
  });
}

export async function appendFolderRestore(authenticatedFetch, masterKey, { clientId }) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'folder_restore',
    clientId
  });
}

export async function appendFileTrash(authenticatedFetch, masterKey, { clientId }) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'file_trash',
    clientId
  });
}

export async function appendFileRestore(authenticatedFetch, masterKey, { clientId }) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'file_restore',
    clientId
  });
}

export async function appendFileDelete(authenticatedFetch, masterKey, { clientId }) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'file_delete',
    clientId
  });
}

export async function appendFileAdd(authenticatedFetch, masterKey, meta) {
  return appendJournalEvent(authenticatedFetch, masterKey, {
    type: 'file_add',
    ...meta
  });
}
